import { useState, useEffect, useCallback, useMemo } from 'react';
import _isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import { FiSearch } from 'react-icons/fi';
import { BiBarcodeReader } from 'react-icons/bi';

import { api } from '../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../utils/common';
import { Loader, toasts } from '../../../ui-component';

import ItemCard from './ItemCard';
import type {
  MainTab,
  CatalogResponse,
  CatalogBranch,
  ActiveCategory,
  CatalogItem,
  ReadymadeOutfit,
  CustomOutfit,
} from '../types';

const MAIN_TABS: { label: string; value: MainTab }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Products', value: 'products' },
  { label: 'Services', value: 'services' },
];

const CatalogSection = () => {
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [mainTab, setMainTab] = useState<MainTab>('ALL');
  const [selectedCategoryTypeId, setSelectedCategoryTypeId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const [isItemsLoading, setIsItemsLoading] = useState(false);

  const boutiqueId = getValueFromLocalStorage('boutique_id');

  useEffect(() => {
    const fetchCatalog = async () => {
      if (_isNil(boutiqueId)) {
        return;
      }

      setIsCatalogLoading(true);
      try {
        const response = await api.getRequest(`api/boutiques/${boutiqueId}/order-types/catalog`);

        if (response.status && response.data) {
          setCatalog(response.data);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'catalog-fetch-error');
        }
      } finally {
        setIsCatalogLoading(false);
      }
    };

    fetchCatalog();
  }, [boutiqueId]);

  const currentBranch: CatalogBranch | null = useMemo(() => {
    if (!catalog) {
      return null;
    }

    return catalog[mainTab];
  }, [catalog, mainTab]);

  const categories: ActiveCategory[] = useMemo(() => {
    return currentBranch?.active_categories ?? [];
  }, [currentBranch]);

  const resolveUrl = useCallback((path: string): string => {
    if (path.startsWith('/api/') || path.startsWith('api/')) {
      return path;
    }

    return path;
  }, []);

  const fetchItems = useCallback(
    async (branch: CatalogBranch | null, categoryTypeId: number | null) => {
      if (!branch) {
        return;
      }

      setIsItemsLoading(true);
      try {
        let url: string;

        if (categoryTypeId === null) {
          url = resolveUrl(branch.ALL_URL);
        } else {
          const cat = branch.active_categories.find((c) => c.category_type_id === categoryTypeId);

          if (!cat) {
            setItems([]);
            setIsItemsLoading(false);

            return;
          }

          url = resolveUrl(cat.resource_uri);
        }

        const response = await api.getRequest(url);

        if (response.status && response.data) {
          const data = response.data;
          const combined: CatalogItem[] = [];

          if (data.ready_made_outfits) {
            const readymadeItems: ReadymadeOutfit[] = data.ready_made_outfits.map(
              (item: ReadymadeOutfit) => ({ ...item, header_info: 'products' as const })
            );
            combined.push(...readymadeItems);
          }

          if (data.custom_outfit_response_list) {
            const serviceItems: CustomOutfit[] = data.custom_outfit_response_list.map(
              (item: CustomOutfit) => ({ ...item, header_info: 'services' as const })
            );
            combined.push(...serviceItems);
          }

          if (mainTab === 'ALL' && categoryTypeId === null && data.products && data.services) {
            const productItems: ReadymadeOutfit[] = (data.products.ready_made_outfits || []).map(
              (item: ReadymadeOutfit) => ({
                ...item,
                header_info: 'products' as const,
              })
            );
            const serviceItems: CustomOutfit[] = (
              data.services.custom_outfit_response_list || []
            ).map((item: CustomOutfit) => ({
              ...item,
              header_info: 'services' as const,
            }));
            combined.push(...serviceItems, ...productItems);
          }

          setItems(combined);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'items-fetch-error');
        }
      } finally {
        setIsItemsLoading(false);
      }
    },
    [mainTab, resolveUrl]
  );

  useEffect(() => {
    if (currentBranch) {
      fetchItems(currentBranch, selectedCategoryTypeId);
    }
  }, [currentBranch, selectedCategoryTypeId, fetchItems]);

  const handleTabChange = useCallback((tab: MainTab) => {
    setMainTab(tab);
    setSelectedCategoryTypeId(null);
    setSearchQuery('');
  }, []);

  const handleCategoryChange = useCallback((categoryId: number | null) => {
    setSelectedCategoryTypeId(categoryId);
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchQuery(query);
      }, 400),
    []
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch]
  );

  const getItemName = (item: CatalogItem): string => {
    if ('display_name' in item) {
      return (item as ReadymadeOutfit).display_name || (item as ReadymadeOutfit).name;
    }

    return (item as CustomOutfit).outfit_name;
  };

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }

    const query = searchQuery.toLowerCase();

    return items.filter((item) => {
      const name = getItemName(item).toLowerCase();

      return name.includes(query);
    });
  }, [items, searchQuery]);

  const handleAddItem = useCallback((_item: CatalogItem) => {
    // Will be wired to order summary cart state later
    // eslint-disable-next-line no-console
    console.log(_item);
  }, []);

  if (isCatalogLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden bg-[#F8F8FD]">
      <p className="text-sm px-5 py-4 my-2  bg-[#FFFFFF] text-[#666666] font-[600]">
        Fast desktop POS for custom tailoring, readymade, fabric, and services
      </p>

      {/* Main Tabs */}
      <div className="flex gap-2 px-5 pb-4">
        {MAIN_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => handleTabChange(tab.value)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              mainTab === tab.value
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search + Scan */}
      <div className="flex items-center gap-3 px-5 pb-4">
        <div className="flex-1 relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search item name / SKU / Category"
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
          />
        </div>
        <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
          Scan <BiBarcodeReader size={18} />
        </button>
      </div>

      {/* Category Chips */}
      <div className="flex flex-wrap gap-2 px-5 pb-4">
        <button
          onClick={() => handleCategoryChange(null)}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            selectedCategoryTypeId === null
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat.category_type_id}
            onClick={() => handleCategoryChange(cat.category_type_id)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors truncate max-w-[180px] ${
              selectedCategoryTypeId === cat.category_type_id
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            }`}
            title={cat.category_name}
          >
            {cat.category_name}
          </button>
        ))}
      </div>

      {/* Item Grid */}
      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {isItemsLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
            No items found
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-4">
            {filteredItems.map((item) => (
              <ItemCard key={item.id} item={item} onAdd={handleAddItem} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogSection;
